import {
	observable,
	action,
	runInAction,
	computed,
	reaction,
	IReactionDisposer,
} from "mobx";
import { Subscription } from "rxjs";

import { EventOffer } from "@gp/offer";
import { ISubscriptionRequest } from "@gp/hub";
import { Sorter, insert } from "@gp/utility";
import { OfferSubscriptionResponse, Sport, Team, Tournament } from "@gp/models";

import { LoaderStore } from "@state/stores/common";
import RootStore from "@state/stores/RootStore";
import FavoritesStore from "@state/stores/offer/shared/FavoritesStore";
import SportStatisticsStore from "../statistics/SportStatisticsStore";
import LivePlayerStatisticsStore from "../statistics/LivePlayerStatisticsStore";
import { GroupOfferStore, Options } from "../GroupOfferStore";
import {
	EventBodyTabs,
	StatisticsGroups,
} from "@data-types/offer/event/EventViewStoreEnums";

import { updateUrlParams } from "@state";
import { analyticsTracker } from "@utils/analytics";
import BetBoosterEventPickStore from "@state/quick-play-store/BetBoosterEventPickStore";
import BettingTipsStore from "@state/quick-play-store/BettingTipsStore";
import { THEME_SETTINGS } from "@theme-settings";
import { convertApiOfferResponseToHubModel } from "@utils/offer/convertApiOfferResponseToHubModel";
import { IOfferService } from "@services/offer/IOfferService";
import PeriodConverter, { Period } from "@state/common/PeriodConverter";
import { offerHistoryService } from "@services/offer/offer-history/OfferHistoryService";
import { prematchOfferService } from "@services/offer/PrematchOfferService";
import TeamStandingsStatsStore from "../statistics/TeamStandingsStatsStore";
import TeamLineupsStore from "../statistics/TeamLineupsStore";

export class AdditionalOfferViewStore extends GroupOfferStore {
	//#region members

	private loader: LoaderStore;
	private subscription: Subscription | null;
	private favoritesStore: FavoritesStore;

	@observable betBuilderAvailableTips: any = observable.map();
	@observable favoritesBettingTypeGroups = {};
	//#endregion

	//#region observable

	@observable eventId: string | null;
	@observable isFetchingData = false;
	@observable isStoreInitialized = false;
	@observable currentTab = EventBodyTabs.Markets;
	@observable displayStatisticsGroup: StatisticsGroups | null = null;
	@observable isButtonToggled = false;

	favoritesBettingTypeReaction: IReactionDisposer | null;
	sportStatisticsStore: SportStatisticsStore;
	playerStatisticsStore: LivePlayerStatisticsStore;
	teamStandingsStatsStore: TeamStandingsStatsStore;
	teamLineupsStore: TeamLineupsStore;

	public storeName: "AdditionalOffer" = "AdditionalOffer";

	@observable bettingTypeGroups = observable.array();
	betBoosterEventPicks: BetBoosterEventPickStore;
	bettingTipsStore: BettingTipsStore;
	isExpanded = false;
	@observable period: string = PeriodConverter.GetDefaultPeriod();
	@observable private _historyEventIds: Set<string> = new Set();

	//#endregion observable

	//#region computed

	@computed get isEmpty() {
		return (
			this.events.length === 0 &&
			!this.isFetchingData &&
			this.isStoreInitialized
		);
	}

	@computed get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get betBuilderBettingType() {
		return this.bettingTypeGroups
			.map((bettingTypeGroup) => {
				const filtered = bettingTypeGroup.bettingTypes.filter(
					//@ts-expect-error
					(bettingType) => bettingType.betBuilder
				);

				return { ...bettingTypeGroup, bettingTypes: filtered };
			})
			.filter(
				(bettingTypeGroup) => bettingTypeGroup.bettingTypes.length > 0
			);
	}

	@computed get firstAvailableStats() {
		if (this.sportStatisticsStore.isMatchStatsAvailable) {
			return StatisticsGroups.LiveStats;
		}

		if (this.sportStatisticsStore.isStreakStatAvailable) {
			return StatisticsGroups.StreakStats;
		}

		if (this.teamLineupsStore.isTeamLineupsAvailable) {
			return StatisticsGroups.TeamLineups;
		}

		if (this.playerStatisticsStore.isPlayerStatsAvailable) {
			return StatisticsGroups.PlayerStats;
		}

		if (this.sportStatisticsStore.isEventInsightsAvailable) {
			return StatisticsGroups.EventInsights;
		}

		if (this.teamStandingsStatsStore.isTeamStandingsStatsAvailable) {
			return StatisticsGroups.TeamStandings;
		}

		if (this.sportStatisticsStore.isFullStatsAvailable) {
			return StatisticsGroups.FullStats;
		}

		return null;
	}

	@computed public get defaultBettingTypeDisplay() {
		return this.bettingTypeGroups
			.map((bettingTypeGroup) => {
				const filtered = bettingTypeGroup.bettingTypes.filter(
					//@ts-expect-error
					(bettingType) => bettingType.isDefaultBettingType
				);

				return { ...bettingTypeGroup, bettingTypes: filtered };
			})
			.filter(
				(bettingTypeGroup) => bettingTypeGroup.bettingTypes.length > 0
			);
	}

	@computed get isOfferHistory() {
		return PeriodConverter.IsOfferHistoryPeriod(this.period);
	}

	public isHistoryEvent(id: string): boolean {
		return this._historyEventIds.has(id);
	}
	//#endregion computed

	//#region  constructor

	constructor(
		rootStore: RootStore,
		favoritesStore: FavoritesStore,
		options: Options = null
	) {
		super(rootStore, options);

		this.loader = new LoaderStore();
		this.favoritesStore = favoritesStore;
		this.sportStatisticsStore = new SportStatisticsStore(this);
		this.betBoosterEventPicks = new BetBoosterEventPickStore(
			this.rootStore
		);
		this.playerStatisticsStore = new LivePlayerStatisticsStore(this);
		this.bettingTipsStore = new BettingTipsStore(
			this.rootStore.quickPlayStore
		);
		this.teamStandingsStatsStore = new TeamStandingsStatsStore(this);
		this.teamLineupsStore = new TeamLineupsStore(
			this.playerStatisticsStore
		);
	}

	//#endregion constructor

	//#region actions

	@action.bound
	openTab(tab: EventBodyTabs) {
		if (this.currentTab === tab) {
			return;
		}

		// close currently open tab
		switch (this.currentTab) {
			case EventBodyTabs.Statistics:
				this.toggleStatistics(false);
				break;
			case EventBodyTabs.MyBets:
				this.toggleMyBets(false);
				break;
		}

		// open another
		switch (tab) {
			case EventBodyTabs.Statistics:
				this.toggleStatistics(true);
				break;
			case EventBodyTabs.MyBets:
				this.toggleMyBets(true);
				break;
		}

		this.currentTab = tab;
	}

	@action.bound
	toggleStatistics(isToggled: boolean) {
		this.setDisplayStatisticsGroup(
			isToggled ? this.firstAvailableStats : null
		);
	}

	@action.bound
	public setDisplayStatisticsGroup(group: StatisticsGroups | null) {
		if (this.displayStatisticsGroup !== group) {
			// close currently open statistics tab
			this.toggleStatisticsGroup(this.displayStatisticsGroup);
			// open another tab instead
			this.toggleStatisticsGroup(group);

			this.displayStatisticsGroup = group;
		}
	}

	@action.bound
	toggleMyBets(isToggled: boolean) {
		this.handleUrls(isToggled);
	}

	@action.bound
	handleUrls(value: boolean, shouldReplace: boolean = true) {
		if (value) {
			updateUrlParams({ myBets: true }, shouldReplace);
		} else {
			updateUrlParams({ myBets: false }, shouldReplace);
		}
	}

	@action.bound
	async updateEventId(
		eventId: string | null,
		isLive: boolean = false,
		period: Period | string = PeriodConverter.GetDefaultPeriod()
	) {
		if (eventId == this.eventId && this.isLive == isLive) {
			return;
		}

		if (eventId == null) {
			this.onDispose();
		} else {
			this.rootStore.betBuilderStore.removeAdditionalOfferStore({
				id: this.eventId,
			});
			this.eventId = eventId;
			this.isLive = isLive;
			this.period = period;
			this.resetDisplayBettingGroup();
			this.bettingTipsStore.onDispose();
			this.betBoosterEventPicks.onDispose();
			this.sportStatisticsStore.onDispose();
			this.playerStatisticsStore.onDispose();
			this.teamLineupsStore.onDispose();

			await this.initializeAdditionalOfferStore();

			const urlParams = new URLSearchParams(window.location.search);
			const isMyBetsToggled = urlParams.get("myBets") === "true";

			if (isMyBetsToggled) {
				this.openTab(EventBodyTabs.MyBets);
			} else {
				this.openTab(EventBodyTabs.Markets);
			}
		}
	}

	//#endregion actions

	//#region data fetching

	async initializeAdditionalOfferStore() {
		this.disposeSubscription();
		this.disposeFavoritesBettingTypeReaction();
		this.readSearchParamsOnLoad();

		if (this.isLive) {
			this._initializeAdditionalOfferSubscription();
		} else {
			await this._initializePrematchAdditionalOffer();
		}

		this.rootStore.betBuilderStore.addAdditionalOfferStore(this);

		this.favoritesBettingTypeReaction = reaction(
			() =>
				JSON.stringify(this.favoritesStore.userFavoriteBettingTypesMap),

			() => {
				this.updateSorting();
			}
		);
	}

	@action.bound
	_initializeAdditionalOfferSubscription() {
		this.disposeSubscription();
		this.disposeFavoritesBettingTypeReaction();

		const eventId = this.eventId;

		if (eventId == null) {
			return;
		}

		this.onStartFetching();

		//#region  subscription request obj

		const subscriptionId = `live-additional-offer-${eventId}`;

		let subscriptionRequest: ISubscriptionRequest = {
			subscriptionId: subscriptionId,
			compress: WEBPACK_OFFER_COMPRESS,
			channels: [
				{
					name: "event",
					filter: {
						id: eventId,
					},
				},
				{
					name: "betOffer",
				},
			],
			throttle: 3,
		};

		//#endregion  subscription request obj

		// call unsubscribe() in order to dispose subscription on the application hub
		this.subscription = this.rootStore.hub
			.getOfferSubscription(subscriptionRequest)
			.subscribe({
				next: (response) => {
					this.handleResponse(response);
				},
				error: (err: any) => {
					console.error(err);
					this.onDoneFetching();
				},
			});
		this.favoritesBettingTypeReaction = reaction(
			() =>
				JSON.stringify(this.favoritesStore.userFavoriteBettingTypesMap),

			() => {
				this.updateSorting();
			}
		);
	}

	@action.bound
	private handleResponse(response: OfferSubscriptionResponse) {
		if (
			response.startingVersion === 0 &&
			response.lookups == null &&
			response.offerChanges == null
		) {
			this.onDispose();

			return;
		}

		runInAction(() => {
			this.assignOfferData(response);
			if (this.isOfferHistory) {
				this._historyEventIds = new Set(this.eventsMap.keys());
			}
			if (response.startingVersion == 0) {
				this.sportStatisticsStore.checkLiveStatisticsAvailability();
				this.playerStatisticsStore.checkLiveStatisticsAvailability();
				this.sportStatisticsStore.checkEventInsightsAvailability();
				this.teamStandingsStatsStore.initializeLiveTeamStandingsStatistics();
				this.teamLineupsStore.checkPrematchTeamLineupsAvailability();

				if (this.event) {
					this.rootStore.betBuilderStore.onAdditionalStoreEnter(
						this.event
					);
					this.sportStatisticsStore.fetchWinLostDrawStats(this.event);
				}
			}
			this.onDoneFetching();
			if (
				response.startingVersion == 0 ||
				response.lookups?.bettingTypes
			) {
				this.sportStatisticsStore.checkBettingTypeStatisticsAvailability();

				if (this.event && this.sport && this.tournament) {
					analyticsTracker.trackMatchViewed(
						this.event?.id,
						this.sport?.abrv,
						this.teamOne?.abrv || "",
						this.teamTwo?.abrv || "",
						this.tournament.abrv
					);
				}
			}

			if (response.startingVersion == 0) {
				this.sportStatisticsStore.checkStreakStatsAvailability();
			}
		});
		runInAction(async () => {
			if (this.event?.betBuilder) {
				if (
					this.betBoosterEventPicks.previousBetBuilderStatus !==
					this.event.betBuilder
				) {
					this.betBoosterEventPicks.previousBetBuilderStatus =
						this.event?.betBuilder || false;
					if (
						this.eventId &&
						!this.betBoosterEventPicks.isAvailable
					) {
						await this.betBoosterEventPicks.onInitialize(
							this.eventId,
							this.event.isLive
						);
					}
				}
			} else {
				if (this.betBoosterEventPicks.isAvailable) {
					this.betBoosterEventPicks.onDispose();
				}
			}
			if (response.startingVersion == 0) {
				const isBettingTipsFeatureEnabled =
					THEME_SETTINGS.features.quickPlayOptions?.includes(
						"betting-tips"
					);
				if (isBettingTipsFeatureEnabled && this.event?.id) {
					await this.bettingTipsStore.onInitialized({
						eventId: this.event.id,
					});
				}
			}
		});
	}

	@action.bound
	async _initializePrematchAdditionalOffer() {
		if (!this.eventId) {
			console.error("AdditionalOfferViewStore, eventId can not be null");
			this.onDoneFetching();
			return;
		}

		this.onStartFetching();

		if (!this.eventId) {
			console.error("AdditionalOfferViewStore, eventId can not be null");
			this.onDoneFetching();
			return;
		}

		try {
			const offer = await this.getService().getEventData({
				filter: {
					id: {
						eq: [this.eventId],
					},
				},
			});
			//@ts-expect-error
			const convertedOffer: OfferSubscriptionResponse = {
				...convertApiOfferResponseToHubModel(offer),
				startingVersion: 0,
			};
			this.handleResponse(convertedOffer);
		} catch (err) {
			console.error(err);
		} finally {
			this.onDoneFetching();
		}
	}

	getService(): IOfferService {
		// period: Period = PeriodConverter.GetDefaultPeriod()
		if (this.isOfferHistory) {
			return offerHistoryService;
		}
		return prematchOfferService;
	}

	@action.bound
	onStartFetching() {
		this.isFetchingData = true;
		this.loader.suspend();
	}

	@action.bound
	private onDoneFetching() {
		/** If There are no events or there are no betting types ( no offer ).
		 *  Close additional offer store. ( this currently only affects twd desktop as mill-v1 does not use url params) */
		this.isFetchingData = false;
		this.loader.resume();
		this.isStoreInitialized = true;
	}

	setBetBuilderAvailableTips(tips: any) {
		if (Array.isArray(tips)) {
			this.betBuilderAvailableTips = new Map(
				tips.reduce((acc, tip) => {
					tip.bettingOffers.forEach((bo: any) =>
						acc.push([bo.id, bo])
					);
					return acc;
				}, [])
			);
		} else {
			this.betBuilderAvailableTips.clear();
		}
	}

	//#endregion data fetching

	//#region sorting
	@action.bound
	public updateSorting() {
		this.events.forEach((event) => {
			// Add sport to event
			// @ts-expect-error
			if (event.sport == null) {
				// @ts-expect-error
				event.sport = this.getMappedSport(event);
			}

			// @ts-expect-error
			const favoritesBettingTypesIds = [];
			const eventKeys = this.eventKeysMap.get(event.id);

			// @ts-expect-error
			if (eventKeys?.size > 0) {
				// Loop over all event keys and add there betting type group to event
				// Also add each unique betting type into betting type group
				// @ts-expect-error
				const btGroups = Array.from(eventKeys.values())
					.reduce((acc, eventKey) => {
						// if it's a favorite
						const isFavoriteBettingGroup =
							this.favoritesStore.isUserFavoriteBettingType(
								eventKey.bettingTypeId,
								eventKey.specifier || null
							);
						if (isFavoriteBettingGroup) {
							const bettingTypeGroupLookup =
								this.lookupsStore.bettingTypeGroups.get(
									eventKey.bettingType.groupId || ""
								);
							if (
								bettingTypeGroupLookup &&
								// @ts-expect-error
								favoritesBettingTypesIds.find(
									(btGroup) =>
										btGroup.abrv ===
										eventKey.bettingType.groupId
								) == null
							) {
								favoritesBettingTypesIds.push({
									...bettingTypeGroupLookup,
									bettingTypes: [],
								});
							}

							//#endregion insert betting type group

							//#region  insert betting type into betting type group
							// @ts-expect-error
							const bettingGroup = favoritesBettingTypesIds.find(
								(btGroup) =>
									btGroup.abrv ===
									eventKey.bettingType.groupId
							);
							if (bettingGroup) {
								if (bettingGroup.bettingTypes == null) {
									bettingGroup.bettingTypes = [];
								}

								if (
									bettingGroup.bettingTypes.find(
										// @ts-expect-error
										(bt) => bt.id === eventKey.bettingTypeId
									) == null
								) {
									insert(
										bettingGroup.bettingTypes,
										eventKey.bettingType,
										Sorter.sort((a, b) => {
											const aSort =
												a?.settingsPerSport?.[
													event.sportId
												]?.sortOrder;
											const bSort =
												b?.settingsPerSport?.[
													event.sportId
												]?.sortOrder;

											const aSortOrder =
												aSort == null ? 99999 : aSort;
											const bSortOrder =
												bSort == null ? 99999 : bSort;

											return Math.sign(
												aSortOrder - bSortOrder
											);
										}, "id")
									);
								}
							}
						}

						//#region insert betting type group
						if (
							acc.find(
								(btGroup) =>
									// @ts-expect-error
									btGroup.abrv ===
									eventKey.bettingType.groupId
							) == null
						) {
							const bettingTypeGroup =
								this.lookupsStore.bettingTypeGroups.get(
									eventKey.bettingType.groupId || ""
								);
							if (bettingTypeGroup) {
								/* @ts-expect-error */
								bettingTypeGroup.bettingTypes = [];
								/* @ts-expect-error */
								acc.push(bettingTypeGroup);
							}
						}

						//#endregion insert betting type group

						//#region  insert betting type into betting type group

						const bettingGroup = acc.find(
							(btGroup) =>
								// @ts-expect-error
								btGroup.abrv === eventKey.bettingType.groupId
						);

						// @ts-expect-error
						if (bettingGroup && bettingGroup.bettingTypes == null) {
							// @ts-expect-error
							bettingGroup.bettingTypes = [];
						}
						if (eventKey.betBuilder) {
							// @ts-expect-error
							eventKey.bettingType.betBuilder =
								eventKey.betBuilder;
						}

						if (
							bettingGroup &&
							// @ts-expect-error
							bettingGroup.bettingTypes.find(
								// @ts-expect-error
								(bt) => bt.id === eventKey.bettingTypeId
							) == null
						) {
							insert(
								// @ts-expect-error
								bettingGroup.bettingTypes,
								eventKey.bettingType,
								Sorter.sort((a, b) => {
									const aSort =
										a?.settingsPerSport?.[event.sportId]
											?.sortOrder;
									const bSort =
										b?.settingsPerSport?.[event.sportId]
											?.sortOrder;

									const aSortOrder =
										aSort == null ? 99999 : aSort;
									const bSortOrder =
										bSort == null ? 99999 : bSort;

									return Math.sign(aSortOrder - bSortOrder);
								}, "id")
							);
						}

						//#endregion insert betting type into betting type group

						return acc;
					}, [])
					.sort(Sorter.sort("sortOrder", "id"));

				this.bettingTypeGroups = observable.array(btGroups);
			}
			// @ts-expect-error
			event.favoritesBettingTypesIds = favoritesBettingTypesIds.sort(
				Sorter.sort("sortOrder", "id")
			);
			this.favoritesBettingTypeGroups = {
				...this.favoritesBettingTypeGroups,
				// @ts-expect-error
				[event.id]: event.favoritesBettingTypesIds,
			};
		});
	}
	//#endregion sorting

	//#region  disposers

	@action.bound
	public onDispose() {
		this.rootStore.betBuilderStore.removeAdditionalOfferStore({
			id: this.eventId,
		});
		this.disposeSubscription();
		this.reset();

		this.eventId = null;
		this.isStoreInitialized = false;
		this.isFetchingData = false;
		this.favoritesBettingTypeGroups = {};
		this.resetDisplayBettingGroup();
		this.period = PeriodConverter.GetDefaultPeriod();
		this.disposeFavoritesBettingTypeReaction();

		if (this.rootStore.platform == "web") {
			this.rootStore.setAdditionalOfferVisible(false);
		}
		this.bettingTypeGroups = observable.array();
		this._historyEventIds.clear();
		this.sportStatisticsStore.onDispose();
		this.playerStatisticsStore.onDispose();
		this.betBoosterEventPicks.onDispose();
		this.bettingTipsStore.onDispose();
		this.teamStandingsStatsStore.onDispose();
		this.teamLineupsStore.onDispose();
	}

	@action.bound
	disposeSubscription() {
		if (this.subscription != null) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	@action.bound
	disposeFavoritesBettingTypeReaction() {
		//maybe it works
		if (this.favoritesBettingTypeReaction != null) {
			this.favoritesBettingTypeReaction();
			this.favoritesBettingTypeReaction = null;
		}
	}

	//#endregion disposers

	/* Helpers */

	@computed get teamOne(): Team | undefined {
		if (
			this.event &&
			this.event.displayValues &&
			this.event.displayValues.teamOneId
		) {
			return this.lookupsStore.teams.get(
				this.event.displayValues.teamOneId
			);
		}
		return undefined;
	}

	@computed get teamTwo(): Team | undefined {
		if (
			this.event &&
			this.event.displayValues &&
			this.event.displayValues.teamTwoId
		) {
			return this.lookupsStore.teams.get(
				this.event.displayValues.teamTwoId
			);
		}
		return undefined;
	}

	@computed get event(): EventOffer | undefined {
		return this.eventsMap.get(this.eventId || "");
	}

	get sport(): Sport | undefined {
		return this.lookupsStore.sports.get(this.event?.sportId || "");
	}

	get sportCategory() {
		return this.lookupsStore.categories.get(
			this.event?.sportCategoryId || ""
		);
	}

	get tournament(): Tournament | undefined {
		return this.lookupsStore.tournaments.get(
			this.event?.tournamentId || ""
		);
	}

	@computed
	get availableStatisticsGroups(): StatisticsGroups[] {
		const groups: StatisticsGroups[] = [];
		if (this.sportStatisticsStore.isMatchStatsAvailable) {
			groups.push(StatisticsGroups.LiveStats);
		}
		if (this.sportStatisticsStore.isStreakStatAvailable) {
			groups.push(StatisticsGroups.StreakStats);
		}
		if (this.teamLineupsStore.isTeamLineupsAvailable) {
			groups.push(StatisticsGroups.TeamLineups);
		}
		if (this.playerStatisticsStore.isPlayerStatsAvailable) {
			groups.push(StatisticsGroups.PlayerStats);
		}
		if (this.sportStatisticsStore.isEventInsightsAvailable) {
			groups.push(StatisticsGroups.EventInsights);
		}

		if (this.teamStandingsStatsStore.isTeamStandingsStatsAvailable) {
			groups.push(StatisticsGroups.TeamStandings);
		}

		return groups;
	}

	@computed
	get shouldShowStatisticsHeader(): boolean {
		return this.availableStatisticsGroups.length > 1;
	}

	private toggleStatisticsGroup(group: StatisticsGroups | null) {
		switch (group) {
			case StatisticsGroups.TeamLineups:
				this.teamLineupsStore.toggleTeamLineups();
				break;
			case StatisticsGroups.StreakStats:
				this.sportStatisticsStore.toggleStreakStats();
				break;
			case StatisticsGroups.LiveStats:
				this.sportStatisticsStore.toggleMatchStat();
				break;
			case StatisticsGroups.PlayerStats:
				this.playerStatisticsStore.togglePlayerStat();
				break;
			case StatisticsGroups.TeamStandings:
				this.teamStandingsStatsStore.toggleTeamStandingsStats();
				break;
			case StatisticsGroups.FullStats:
				this.sportStatisticsStore.toggleFullStat();
				break;
		}
	}
}
